import { getAccessToken } from '#lib/authToken';
import { fetcher } from '#lib/backendFetcher';

const BACKEND = process.env.REACT_APP_BACKEND_WORKER_URL;

/**
 * POST a multipart/form-data body to a CRM import endpoint. Bypasses the shared
 * `fetcher` (which sends JSON); injects the JWT and lets the browser set the
 * multipart boundary. Throws an Error with `status`/`code`/`validationErrors`.
 *
 * @param {string} path - API path (e.g. '/api/v1/crm/import/analyze')
 * @param {FormData} form - the multipart body
 * @returns {Promise<Object>} parsed JSON response
 */
async function postMultipart(path, form) {
	const token = await getAccessToken();
	const res = await fetch(`${BACKEND}${path}`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: form,
	});

	if (!res.ok) {
		let errorData = {};
		try {
			errorData = await res.json();
		} catch {
			errorData = { errorMessage: `Request failed: ${res.status}` };
		}
		const error = new Error(errorData.errorMessage || `Request failed: ${res.status}`);
		error.status = res.status;
		error.code = errorData.errorCode ?? null;
		error.validationErrors = errorData.validationErrors ?? null;
		throw error;
	}

	return res.json();
}

/**
 * Upload a CSV for analysis → mapping suggestions + available target fields.
 * @param {File} file - the CSV file
 * @returns {Promise<{ suggestedMapping: Object, availableTargetFields: Object[] }>}
 */
export function analyzeCrmImport(file) {
	const form = new FormData();
	form.append('file', file);
	return postMultipart('/api/v1/crm/import/analyze', form);
}

/**
 * Upload a CSV + mapping + mode to start the import (sync or async).
 * @param {Object} params
 * @param {File} params.file - the CSV file
 * @param {Object} params.columnMapping - { targetField: csvColumnHeader }
 * @param {string} params.mode - 'create' | 'update' | 'upsert'
 * @param {string} [params.pipelineId] - optional: assign each company to this pipeline (create a deal)
 * @param {string} [params.stageId] - required when pipelineId is set
 * @param {string[]} [params.applyTagIds] - optional: tag ids to assign to every imported company
 * @param {boolean} [params.fuzzyNameMatch] - optional: dedup email-less contacts by full name within their company
 * @param {string} [params.contactListId] - optional: add every row's contact (or company) to this contact list
 * @returns {Promise<Object>} import result, or { status: 'processing', jobId }
 */
export function startCrmImport({
	file,
	columnMapping,
	mode,
	pipelineId,
	stageId,
	applyTagIds,
	fuzzyNameMatch,
	contactListId,
}) {
	const form = new FormData();
	form.append('file', file);
	form.append('columnMapping', JSON.stringify(columnMapping));
	form.append('mode', mode);
	if (pipelineId && stageId) {
		form.append('pipelineId', pipelineId);
		form.append('stageId', stageId);
	}
	if (applyTagIds?.length) {
		form.append('applyTagIds', JSON.stringify(applyTagIds));
	}
	if (fuzzyNameMatch) {
		form.append('fuzzyNameMatch', 'true');
	}
	if (contactListId) {
		form.append('contactListId', contactListId);
	}
	return postMultipart('/api/v1/crm/import/start', form);
}

/**
 * Poll async import job status.
 * @param {string} jobId - the import job id
 * @returns {Promise<Object>} job status with progress
 */
export const getCrmImportJob = (jobId) => fetcher(`/api/v1/crm/import/jobs/${jobId}`);

// Pipedrive migration (multi-file)

/**
 * Upload all Pipedrive export CSVs to be classified + staged.
 * @param {File[]} files - the Pipedrive .csv files
 * @returns {Promise<Object>} { batchId, locale, recognised, ignored, totalRows, pipelinesPreview }
 */
export function analyzePipedriveImport(files) {
	const form = new FormData();
	for (const f of files) form.append('files', f);
	return postMultipart('/api/v1/crm/import/pipedrive/analyze', form);
}

/**
 * Start the Pipedrive migration for a previously analyzed batch.
 * @param {string} batchId - the batch id returned by analyze
 * @returns {Promise<Object>} { status: 'processing', jobId, totalRows }
 */
export const startPipedriveImport = (batchId) =>
	fetcher('/api/v1/crm/import/pipedrive/start', { method: 'POST', body: { batchId } });

/**
 * Poll a Pipedrive migration job.
 * @param {string} jobId - the migration job id
 * @returns {Promise<Object>} job status with per-entity summary
 */
export const getPipedriveImportJob = (jobId) => fetcher(`/api/v1/crm/import/pipedrive/jobs/${jobId}`);
